// Detalle del equipo en «Elige el teléfono»: ficha en dos columnas, cómo se paga y las promos por tipo.
// Se pinta desde la selección local, así sigue visible aunque el equipo apartado salga del catálogo.
import type { ReactNode } from 'react';
import { Package, Smartphone } from 'lucide-react';
import { formatMXN } from '@/shared/lib/format/money';
import { formatStorage } from '@/shared/lib/format/capacity';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { financiersFor } from '../cardBenefits';
import { creditPromotionRows, type CreditPromotionRow } from '../creditPromotions';
import type { RenderAccountBonuses } from '../promotionSlots';
import type { CatalogItem, PlanLevel, ProductCommercialInfo, ReservationState } from '../types';
import { CompactCreditPromotions } from './CompactCreditPromotions';
import { CompactPromotionGroup, CompactPromotionRow } from './CompactPromotionGroup';
import { FinancierBadge } from './FinancierBadge';
import { StockChip, ToneChip } from './StockChip';

/** Promo sobre accesorios que se activa con este teléfono; solo informativa. */
export interface AccessoryPromotionNote {
    promotionId: string;
    name: string;
}

const LABEL = 'text-[10px] uppercase tracking-[0.04em] text-muted-foreground';

function SpecRow({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex items-baseline justify-end gap-1.5">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="font-semibold">{children}</dd>
        </div>
    );
}

export function ProductDetailCard({
    picked,
    stock,
    service = false,
    reservation,
    imei,
    clientLevel,
    salePromotions,
    promotionPicker,
    downPayment = null,
    creditPromotions,
    creditNote,
    accessoryPromotions = [],
    footer,
    kitComponents,
    onShowKitContents,
}: {
    /** Con `financeable`/`affordableOnCredit` del catálogo, «Financian» dice lo mismo que la tarjeta. */
    picked:
        | (ProductCommercialInfo &
              Partial<Pick<CatalogItem, 'financeable' | 'affordableOnCredit' | 'ram'>>)
        | null;
    /** Stock vivo del SKU. `null` = ya no está en el catálogo (típicamente porque lo apartaste tú). */
    stock: number | null;
    /** Un servicio no lleva inventario: el panel no habla de stock. */
    service?: boolean;
    /** Estado de la reserva del SKU: `held` retiene una unidad, `expired` la perdió a los 30 min. */
    reservation: ReservationState;
    /** IMEI de la unidad apartada — el número de la caja que el vendedor tiene que bajar del anaquel. */
    imei: string | null;
    /** Nivel del cliente (score adelantado). Sin él, el panel muestra todos los grupos como condicionales. */
    clientLevel?: PlanLevel | null;
    /** Bloques de Promociones; pinta «Bonos a tu cuenta» con `renderBonuses` para juntar los de crédito. */
    salePromotions?: (renderBonuses: RenderAccountBonuses) => ReactNode;
    /** Selector «Promo del equipo»; va arriba de las promos de solo lectura. */
    promotionPicker?: ReactNode;
    /** Enganche con SureKeep y de dónde sale; `null` si no se sabe o no lo financia. */
    downPayment?: { cents: number; note: string | null } | null;
    /** Promos de crédito de este equipo; sin ellas se leen las del catálogo del equipo. */
    creditPromotions?: readonly CreditPromotionRow[];
    /** Sin nivel resuelto se muestran «según su nivel»; si no califica, el grupo lo dice. */
    creditNote?: 'levelPending' | 'notEligible' | undefined;
    /** Promos sobre accesorios que se activan con este teléfono (se aplican en el paso 05). */
    accessoryPromotions?: readonly AccessoryPromotionNote[];
    /** Pie del panel: campo de cupón y desglose del precio. */
    footer?: ReactNode;
    /** Componentes del kit elegido; con el handler muestra «Ver qué incluye». */
    kitComponents?: number;
    onShowKitContents?: () => void;
}) {
    const financiers = picked ? financiersFor(picked) : [];
    const storage = picked ? formatStorage(picked.capacity) : '';
    const ram = picked ? formatStorage(picked.ram) : '';
    return (
        <aside className="rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
                <div className="text-[13px] font-semibold">Detalle del equipo</div>
            </div>

            <div className="p-4">
                {picked ? (
                    <>
                        <div className="mb-3 flex items-start gap-3">
                            <div className="flex min-w-0 flex-1 items-start gap-3">
                                <Smartphone
                                    className="size-10 flex-none text-muted-foreground"
                                    aria-hidden
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                                        {picked.brand}
                                    </div>
                                    <div className="text-[15px] font-semibold leading-tight">
                                        {picked.model !== ''
                                            ? picked.model
                                            : itemDisplayName(picked)}
                                    </div>
                                    {onShowKitContents !== undefined &&
                                        kitComponents !== undefined && (
                                            <button
                                                type="button"
                                                onClick={onShowKitContents}
                                                className="mt-1 rounded-sm text-[12px] font-semibold text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                            >
                                                Ver qué incluye ({kitComponents})
                                            </button>
                                        )}
                                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                        {service ? (
                                            <StockChip
                                                item={{ itemType: 'SERVICE', stock: null }}
                                            />
                                        ) : stock !== null ? (
                                            <StockChip item={{ stock }} />
                                        ) : (
                                            <ToneChip tone="warning">Fuera de catálogo</ToneChip>
                                        )}
                                        {reservation === 'held' ? (
                                            <ToneChip tone="success">Apartado</ToneChip>
                                        ) : reservation === 'expired' ? (
                                            <ToneChip tone="warning">La reserva venció</ToneChip>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                            <dl className="flex flex-none flex-col gap-0.5 text-right text-[11px]">
                                {storage !== '' && <SpecRow label="Memoria">{storage}</SpecRow>}
                                {ram !== '' && <SpecRow label="RAM">{ram}</SpecRow>}
                                {picked.color.trim() !== '' && (
                                    <SpecRow label="Color">{picked.color}</SpecRow>
                                )}
                                <SpecRow label="SKU">
                                    <span className="font-mono font-normal">{picked.sku}</span>
                                </SpecRow>
                                {imei !== null && (
                                    <SpecRow label="IMEI">
                                        <span className="font-mono text-[10.5px] font-normal">
                                            {imei}
                                        </span>
                                    </SpecRow>
                                )}
                            </dl>
                        </div>

                        <div className="my-3 h-px bg-border" />

                        <dl
                            aria-label="Cómo se paga"
                            className="grid grid-cols-[auto_1fr] items-baseline gap-x-4 gap-y-2 text-[12px]"
                        >
                            {downPayment !== null && (
                                <>
                                    <dt className={LABEL}>Enganche</dt>
                                    <dd className="flex flex-wrap items-baseline gap-x-2 tabular-nums">
                                        <strong className="font-semibold">
                                            {formatMXN(downPayment.cents / 100)}
                                        </strong>
                                        {downPayment.note && (
                                            <span className="text-[11px] text-muted-foreground">
                                                {downPayment.note}
                                            </span>
                                        )}
                                    </dd>
                                </>
                            )}
                            <dt className={LABEL}>Financian</dt>
                            <dd className="flex flex-wrap gap-1">
                                {financiers.length > 0
                                    ? financiers.map((code) => (
                                          <FinancierBadge key={code} code={code} />
                                      ))
                                    : 'Solo de contado'}
                            </dd>
                        </dl>

                        <div key={picked.sku}>
                            {promotionPicker}
                            {salePromotions?.(() => null)}
                            <CompactCreditPromotions
                                rows={
                                    creditPromotions ??
                                    (financiers.length === 0
                                        ? []
                                        : creditPromotionRows([], [picked], clientLevel))
                                }
                                clientLevel={clientLevel}
                                note={creditNote}
                            />
                            <CompactPromotionGroup
                                label="Sobre accesorios"
                                icon={Package}
                                tone="accessory"
                                limit={Number.POSITIVE_INFINITY}
                                items={[...accessoryPromotions]
                                    .sort((a, b) => a.name.localeCompare(b.name, 'es-MX'))
                                    .map((promo) => ({
                                        key: promo.promotionId,
                                        node: (
                                            <CompactPromotionRow
                                                icon={Package}
                                                tone="accessory"
                                                name={promo.name}
                                            />
                                        ),
                                    }))}
                            />
                        </div>
                    </>
                ) : (
                    <div className="py-6 text-center text-[12.5px] text-muted-foreground">
                        Toca un equipo para ver sus promos.
                    </div>
                )}
            </div>
            {picked && footer !== undefined && footer !== null && (
                <div className="border-t border-border px-4 py-3">{footer}</div>
            )}
        </aside>
    );
}
